import { useEffect, useMemo } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { useAlleNachrichten } from '../../hooks/buch'
import { useMembers, useTrip } from '../../hooks/queries'
import { Beitrag } from './Buchteile'
import { imZeitraum, istGesetzt, zeitraumAus, zeitraumText } from './zeitraum'
import { ExportButtons } from './ExportButtons'
import { tripPeriod } from '../../lib/format'
import { buchTag } from '../../lib/ortszeit'
import { BackIcon } from '../../components/Icons'
import { useTexte } from '../../texte'
import '../../styles/buch.css'

/**
 * Das private Gespräch mit einem Menschen, als eigenes Dokument (#14).
 *
 * **Warum je Person eines und nicht ein gemeinsames.** Ein Buch ist dazu da,
 * verschenkt zu werden — das ist sein Zweck. Ein einziges „privat"-Dokument
 * mit allen Gesprächen darin wäre genau das Leck: Annas Antworten landen bei
 * Bert, sobald die Datei weitergereicht wird. Je Person ist die Datei von sich
 * aus an eine Beziehung adressiert, und Dateiname und erste Seite sagen es.
 *
 * **Der Filter hier verengt und erweitert nie.** Der Server liefert zu
 * `?with=X` auch die Beiträge an alle, als Zusammenhang — im Buch stehen die
 * schon, und ein zweites Mal wären sie nur Umfang. Weggelassen wird also
 * etwas, das erlaubt wäre; hinzugefügt wird nichts. Der Zusammenhang bleibt
 * trotzdem: `reply_to` trägt das zitierte Stück mit.
 */
export function PrivatbuchPage() {
  const { tripId, userId } = useParams()
  const id = Number(tripId)
  const wer = Number(userId)
  const [suche] = useSearchParams()

  const trip = useTrip(id)
  const empfaenger = useMembers(id, true)
  const stand = useAlleNachrichten(id, wer)

  const t = useTexte().buch
  const versatz = trip.data?.tz_offset_min ?? null
  const person = (empfaenger.data ?? []).find((m) => m.user.id === wer)
  const name = person?.user.display_name ?? t.privat.diesePerson

  /**
   * **Derselbe Zeitraum wie beim Buch, und aus demselben Grund an einer
   * Stelle.** Die Infoseite bietet ihn über beiden Verweisen an; er dürfte
   * nicht für das eine gelten und für das andere stillschweigend nicht.
   */
  const zeitraum = useMemo(() => zeitraumAus(suche), [suche])
  const passt = useMemo(() => imZeitraum(zeitraum, versatz), [zeitraum, versatz])

  const briefe = stand.nachrichten
    .filter((m) => m.kind !== 'broadcast')
    .filter((m) => passt(m.created_at))

  // Der Fenstertitel wird im Druckdialog zum Dateinamen. Er nennt die Person,
  // damit niemand das falsche Dokument weiterreicht.
  useEffect(() => {
    const alt = document.title
    if (trip.data !== undefined) document.title = t.privat.dateiname(trip.data.title, name)
    return () => {
      document.title = alt
    }
  }, [trip.data, name, t])

  return (
    // Leiste neben dem Scrollkasten und nicht darin -- der Grund steht in
    // `BuchPage`, wo dieselbe Umstellung ausführlich begründet ist.
    <div className="buchseite">
      <div className="buch__leiste buch__nurbildschirm">
        <Link className="icon-button" to={`/t/${id}/info`} aria-label={t.zurueck}>
          <BackIcon />
        </Link>
        <span className="bar__grow">
          {stand.laeuft ? t.zusammenstellen(stand.seiten) : t.nachrichten(briefe.length)}
        </span>
        {stand.fehler !== null && (
          <p className="notice notice--eng" role="alert">
            {stand.fehler}
          </p>
        )}
        <ExportButtons
          tripId={id}
          withUser={wer}
          from={zeitraum.von}
          to={zeitraum.bis}
          ready={!stand.laeuft && stand.fehler === null}
          waitingText={stand.laeuft ? t.nochNichtFertig : t.nichtBereit}
        />
      </div>

      {/* **Die eigene Fußzeile.** Sie wiederholt sich beim Drucken auf jeder
          Seite; Safaris eigene Zeile mit Adresse und Seitenzahl steht daneben
          im Seitenrand und lässt sich nur im Druckdialog abschalten. */}
      <p className="buch__fuss">{t.privat.fuss(trip.data?.title ?? t.reise, name)}</p>

      <div className="buch">
        <section className="buch__deckel">
          <h1>{trip.data?.title ?? t.reise}</h1>
          <p>{tripPeriod(trip.data?.starts_on ?? null, trip.data?.ends_on ?? null)}</p>
          <p>
            <strong>{t.privat.deckel(name)}</strong>
          </p>
          {istGesetzt(zeitraum) && (
            <p className="buch__ausschnitt">
              <strong>{t.ausschnitt(zeitraumText(zeitraum))}</strong>
            </p>
          )}
          <p className="buch__warnung">
            {t.privat.warnungVor}
            <strong>{t.privat.warnungNicht}</strong>
            {t.privat.warnungNach(name)}
          </p>
        </section>

        <section className="buch__abschnitt">
          <h2>{t.privat.briefwechsel}</h2>
          {briefe.length === 0 && (
            <p>
              {istGesetzt(zeitraum) ? t.privat.leerImZeitraum : t.privat.leer}
            </p>
          )}
          {briefe.map((nachricht, i) => {
            const vorher = briefe[i - 1]
            const neuerTag =
              vorher === undefined
              || buchTag(vorher.created_at, versatz) !== buchTag(nachricht.created_at, versatz)
            return (
              <div key={nachricht.id}>
                {neuerTag && (
                  <p className="buch__tag">{buchTag(nachricht.created_at, versatz)}</p>
                )}
                <p className="buch__zeit">
                  {nachricht.kind === 'direct' ? t.privat.du : nachricht.author.display_name}
                </p>
                {nachricht.reply_to !== null && (
                  <p className="buch__zitat">{t.privat.zitat(nachricht.reply_to.body)}</p>
                )}
                <Beitrag nachricht={nachricht} versatz={versatz} />
              </div>
            )
          })}
        </section>
      </div>
    </div>
  )
}
